import React, { useEffect, useState } from "react";
import { Camera, Save, Mail, CheckCircle2, AlertCircle, Award, Trophy, Clock, LogIn, BookOpen, Lock } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BADGE_ICON = { "log-in": LogIn, "clock": Clock, "book-open": BookOpen, "trophy": Trophy };

export default function Profile() {
  const { user, refresh } = useAuth();
  const [p, setP] = useState(null);
  const [badges, setBadges] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile/me").then((r) => setP({ skills: [], links: [], ...r.data }));
    api.get("/my/badges").then((r) => setBadges(r.data)).catch(() => {});
  }, []);
  if (!p) return <Loader />;

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile/me", {
        full_name: p.full_name, display_name: p.display_name, niche: p.niche, country: p.country,
        skills: typeof p.skills === "string" ? p.skills.split(",").map((s) => s.trim()).filter(Boolean) : p.skills,
        linkedin: p.linkedin, upwork: p.upwork, bio: p.bio, language: p.language,
      });
      toast.success("Profile saved"); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/profile/me/photo", fd); set("photo_url", data.url); await refresh(); toast.success("Photo updated"); }
    catch { toast.error("Upload failed"); }
  };

  const resendVerify = async () => { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-heading text-3xl font-bold mb-6">My Profile</h1>

      <div className="bg-white border border-border rounded-2xl shadow-sm p-5 mb-5" data-testid="theme-picker">
        <p className="font-medium text-sm mb-3">Accent color theme <span className="text-muted-foreground font-normal">(personalizes your view only)</span></p>
        <div className="flex gap-3">
          {[{ k: "", label: "Teal", c: "#1DB87A" }, { k: "green", label: "Green", c: "#2ECC71" }, { k: "orange", label: "Orange", c: "#F97316" }, { k: "slate", label: "Black/White", c: "#1E293B" }].map((t) => {
            const activeTheme = (typeof localStorage !== "undefined" ? (localStorage.getItem("cr_theme") || "") : "") === t.k;
            return (
              <button key={t.k} type="button" data-testid={`theme-${t.label.toLowerCase()}`}
                onClick={() => { localStorage.setItem("cr_theme", t.k); document.documentElement.dataset.theme = t.k; api.put("/profile/me", { theme: t.k }).catch(() => {}); toast.success(`Theme: ${t.label}`); setP((x) => ({ ...x })); }}
                className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${activeTheme ? "ring-2 ring-offset-2 ring-foreground border-white" : "border-white"}`}
                style={{ background: t.c }} title={t.label} />
            );
          })}
        </div>
      </div>

      {!user?.is_verified && (
        <div className="bg-warning/10 border border-warning/30 rounded-2xl p-4 mb-5 flex items-center justify-between gap-3" data-testid="verify-banner">
          <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-warning" /><p className="text-sm">Please verify your email address.</p></div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={resendVerify} data-testid="resend-verify"><Mail className="w-4 h-4 mr-1.5" /> Resend</Button>
        </div>
      )}

      <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-5 mb-6">
          <div className="relative">
            {p.photo_url ? <img src={fileUrl(p.photo_url)} alt="" className="w-20 h-20 rounded-full object-cover" data-testid="profile-photo" />
              : <div className="w-20 h-20 rounded-full bg-teal/10 text-teal flex items-center justify-center text-2xl font-heading font-bold">{p.full_name?.[0] || user?.name?.[0]}</div>}
            <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center cursor-pointer shadow" data-testid="photo-upload-btn">
              <Camera className="w-4 h-4" />
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
            </label>
          </div>
          <div>
            <p className="font-heading font-bold text-lg flex items-center gap-2">{p.full_name || user?.name} {user?.is_verified && <CheckCircle2 className="w-4 h-4 text-brandgreen" />}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Full name *</Label><Input value={p.full_name || ""} onChange={(e) => set("full_name", e.target.value)} data-testid="pf-name" /></div>
          <div><Label>Display name / Nickname</Label><Input value={p.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="pf-display" /></div>
          <div><Label>VA Niche / Focus *</Label><Input value={p.niche || ""} onChange={(e) => set("niche", e.target.value)} placeholder="e.g. Social Media Management" data-testid="pf-niche" /></div>
          <div><Label>Country / Location</Label><Input value={p.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="pf-country" /></div>
          <div className="sm:col-span-2"><Label>Skills (comma separated)</Label><Input value={Array.isArray(p.skills) ? p.skills.join(", ") : p.skills || ""} onChange={(e) => set("skills", e.target.value)} data-testid="pf-skills" /></div>
          <div><Label>LinkedIn</Label><Input value={p.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} data-testid="pf-linkedin" /></div>
          <div><Label>Upwork Profile</Label><Input value={p.upwork || ""} onChange={(e) => set("upwork", e.target.value)} data-testid="pf-upwork" /></div>
          <div className="sm:col-span-2"><Label>Short bio</Label><Textarea value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} data-testid="pf-bio" /></div>
        </div>

        <Button className="rounded-full bg-teal hover:bg-teal/90 mt-6" onClick={save} disabled={busy} data-testid="save-profile"><Save className="w-4 h-4 mr-1.5" /> Save Profile</Button>
      </div>

      <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8 mt-5" data-testid="badges-card">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Award className="w-5 h-5 text-teal" /> Activity Badges</h2>
        <p className="text-sm text-muted-foreground mt-1">Earned through real activity — they'll unlock special perks in future features.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {badges.map((b) => {
            const I = BADGE_ICON[b.icon] || Award;
            return (
              <div key={b.key} data-testid={`badge-${b.key}`} className={`rounded-2xl border p-4 text-center ${b.earned ? "border-teal/30 bg-teal/5" : "border-border bg-secondary/40 opacity-60"}`}>
                <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center ${b.earned ? "bg-gradient-to-br from-teal to-brandgreen text-white" : "bg-border text-muted-foreground"}`}>
                  {b.earned ? <I className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
                </div>
                <p className="font-heading font-semibold text-sm mt-2">{b.name}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{b.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
